import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import {
    LgvClientService,
    LgvTSHelper,
    NotificationService
} from '../../core/core.module';
import { ActivatedRoute, Router } from '@angular/router';
import { LgvModel } from '../../data-models/longevity.model';
import {
    BehaviorSubject,
    filter,
    map,
    Observable,
    of,
    Subject,
    take,
    takeUntil
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { LockRequestModalComponent } from '../lock-request-modal/lock-request-modal.component';
import { environment as env } from '../../../environments/environment';
import { LgvPricingService } from '../../features/pricing/services/pricing.service';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';
import { LoanAppPageService } from '../../core/services/page-services/loan-app-page.service';
import { LgvLoanApplicationService } from '../../core/services/lgv-loan-application.service';

@Component({
    selector: 'lgv-pricing-scenario-viewer',
    templateUrl: './pricing-scenario-viewer.component.html',
    styleUrls: ['./pricing-scenario-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PricingScenarioViewerComponent implements OnInit, OnDestroy {
    @Input() clientID: string;
    @Input() property: LgvModel.Property;
    @Input() isPropertyless: boolean;
    @Input() loanId: string;
    @Input() agentSS: LgvModel.AgentSS;
    @Input() pricingScenarios$: BehaviorSubject<
        LgvPricingModel.PricingScenario[]
    >;
    @Input() opportunities$: BehaviorSubject<LgvModel.Opportunity[]>;

    isLoading$ = new BehaviorSubject(false);
    showScenarioSection$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();

    get filteredScenarios$(): Observable<LgvPricingModel.PricingScenario[]> {
        return this.pricingScenarios$.pipe(
            map(arr =>
                arr.filter(
                    ps =>
                        !this.property?.propertyInfo?.id ||
                        ps.propertyId === this.property?.propertyInfo?.id
                )
            )
        );
    }
    get filteredOpportunities$(): Observable<LgvModel.Opportunity[]> {
        return this.opportunities$.pipe(
            filter(f => !!f),
            map(arr =>
                arr.filter(
                    o =>
                        o.opportunityInfo?.pricingScenarioId ===
                        this.selectedPricingScenario?.id
                )
            )
        );
    }

    selectedPricingScenario: LgvPricingModel.PricingScenario = null;
    isProd = env.envName === 'PROD';

    constructor(
        private router: Router,
        private lgvPricingSvc: LgvPricingService,
        private notificationSvc: NotificationService,
        public matDialog: MatDialog,
        public lgvClientSvc: LgvClientService,
        private lgvLoanAppPageSvc: LoanAppPageService,
        private loanAppSvc: LgvLoanApplicationService
    ) {}

    ngOnInit(): void {
        this.pricingScenarios$
            .pipe(
                filter(f => !!f),
                takeUntil(this.onDestroy$)
            )
            .subscribe(x => {
                if (x?.length > 0) {
                    this.selectedPricingScenario = x[0];
                } else {
                    this.selectedPricingScenario = null;
                }
            });

        //this.lgvClientSvc.SelectedProperty$.pipe(filter(f => !!f), takeUntil(this.onDestroy$)).subscribe(x => {
        //    console.log('** SelectedProperty$ **');
        //    console.log(x);
        //    console.log(`${x?.propertyInfo?.address} | ${x?.propertyInfo?.city} | ${x?.propertyInfo?.state} | ${x?.propertyInfo?.zip}` )
        //});

        //this.lgvClientSvc.pricingScenarios$.pipe(filter(f => !!f), takeUntil(this.onDestroy$)).subscribe(x => {
        //    console.log('** pricingScenarios$ **');
        //    console.log(x);
        //});

        //this.lgvClientSvc.allOpportunities$.pipe(filter(f => !!f), takeUntil(this.onDestroy$)).subscribe(x => {
        //    console.log('** allOpportunities$ **');
        //    this.printOpportunities(x);
        //});

        //this.lgvClientSvc.scenariosForSelectedProperty$.pipe(filter(f => !!f), takeUntil(this.onDestroy$)).subscribe(x => {
        //    console.log('** scenariosForSelectedProperty$ **');
        //    console.log(x);
        //    for (let ps of x) {
        //        console.log(ps.name);
        //    }
        //});

        //this.lgvClientSvc.opportunitiesForSelectedProperty$.pipe(filter(f => !!f), takeUntil(this.onDestroy$)).subscribe(x => {
        //    console.log('** opportunitiesForSelectedProperty$ **');
        //    this.printOpportunities(x);
        //});

        //this.lgvClientSvc.Opportunities$.subscribe(x => {
        //    this.printOpportunities(x);
        //});
    }
    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    printOpportunities(opps: LgvModel.Opportunity[]) {
        if (!opps) {
            return;
        }

        console.log('### Printing Opportunities: ###');
        for (let o of opps) {
            this.printOpportunity(o);
        }
        console.log('');
    }

    printOpportunity(o: LgvModel.Opportunity) {
        console.log(
            `${o.opportunityInfo?.id} | ${o.opportunityInfo?.pricingScenarioId} | ${o.opportunityInfo?.productName}`
        );
    }

    onAddScenario() {
        this.router.navigate([
            `/admin/pricing-scenario/agent/${this.agentSS.agentInfo.id}/client/${this.clientID}`
        ]);
    }

    onCreatePricingScenario() {
        if (!this.loanId) {
            this.router.navigate([
                `/admin/pricing-scenario/agent/${this.agentSS.agentInfo.id}/client/${this.clientID}/property/${this.property?.propertyInfo?.id}`
            ]);
        } else {
            this.router.navigate([
                `/admin/pricing-scenario/agent/${this.agentSS.agentInfo.id}/client/${this.clientID}/property/${this.property?.propertyInfo?.id}/loan/${this.loanId}`
            ]);
        }
    }

    onScenarioClick(scenarioID: string) {
        this.router.navigate([
            `/admin/pricing-scenario/agent/${this.agentSS.agentInfo.id}/client/${this.clientID}/scenario/${scenarioID}`
        ]);
    }

    onDeleteScenario(ps: LgvPricingModel.PricingScenario) {
        this.isLoading$.next(true);

        this.lgvPricingSvc
            .deleteScenario(ps.id)
            .pipe(take(1))
            .subscribe(
                x => {
                    this.lgvClientSvc.loadScenarios(this.clientID);
                    this.isLoading$.next(false);
                },
                err => {
                    this.isLoading$.next(false);
                    console.log('Error deleting scenario:');
                    console.log(err);
                    this.notificationSvc.error(
                        'There was an error trying to delete the scenario'
                    );
                }
            );
    }

    onDuplicateScenario(origPS: LgvPricingModel.PricingScenario) {
        let currentTime = new Date();
        let curJSTime = LgvTSHelper.jsDateToJsonDate(currentTime);

        let clonePS = { ...origPS };
        clonePS.name = this.findDuplicateName(clonePS.name);
        clonePS.id = null;
        clonePS.createdTimestamp = curJSTime;
        clonePS.updatedTimestamp = curJSTime;

        this.isLoading$.next(true);
        this.lgvPricingSvc
            .addPricingScenario(clonePS)
            .pipe(take(1))
            .subscribe(
                x => {
                    this.isLoading$.next(false);
                    this.notificationSvc.success('Scenario has been copied');

                    this.lgvClientSvc.loadScenarios(this.clientID);
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error copying the scenario: ' + err
                    );
                }
            );
    }

    findDuplicateName(origName: string): string {
        let highNum = 0;
        let prefix = origName;

        let copyIndex = origName.lastIndexOf(' (copy ');
        if (copyIndex !== -1) {
            prefix = origName.substr(0, copyIndex);
        }

        let existingScenarios = this.pricingScenarios$.value;
        if (existingScenarios && existingScenarios.length > 0) {
            for (let ps of existingScenarios) {
                let name = ps.name as string;

                if (
                    name &&
                    name.includes('(copy') &&
                    name.startsWith(prefix) &&
                    name.endsWith(')')
                ) {
                    if (name.endsWith('(copy)')) {
                        // treat this like (copy 1)
                        highNum = Math.max(highNum, 1);
                    } else {
                        let tokens = name.split(' ');
                        if (tokens && tokens.length > 0) {
                            let lastToken = tokens[tokens.length - 1];

                            // strip the trailing ")"
                            lastToken = lastToken.substr(
                                0,
                                lastToken.length - 1
                            );

                            let numTest = +lastToken;
                            let isNumeric = !isNaN(numTest);
                            if (isNumeric) {
                                highNum = Math.max(highNum, numTest);
                            }
                        }
                    }
                }
            }
        }

        if (highNum > 0) {
            return prefix + ` (copy ${++highNum})`;
        } else {
            return origName + ' (copy)';
        }
    }

    OnSelectedPricingScenarioChanged() {
        console.log('OnSelectedPricingScenarioChanged:');
    }

    onRefreshOpportunity(ps: LgvPricingModel.PricingScenario) {
        this.lgvClientSvc.LoadOpportunitiesForPricingScenario(ps, true);
    }

    onSendLockRequest(ps: LgvPricingModel.PricingScenario) {
        const loanApps = this.loanAppSvc.clientLoanApplication$.value;
        const dialogRef = this.matDialog.open(LockRequestModalComponent, {
            width: '750px',
            disableClose: true,
            data: {
                agentSS: this.agentSS,
                pricingScenario: ps,
                loanApplication: loanApps.find(
                    la => la?.id == ps?.loanApplicationId
                )
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {});
    }

    onPriceThisLoan(ps: LgvPricingModel.PricingScenario) {
        const url = `/#/admin/pricing-search/client/${this.lgvClientSvc.ClientID$.value}/property/${ps.propertyId}/loan/${ps.loanId}`;
        window.open(url, '_blank');
    }
}
